import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './Style.css';
import './media.css';

import { Suspense, Fragment, lazy, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import SidebarLayout from "./Layout/SidebarLayout";
import PrivateGuard from './Guard/PrivateGuard';
import { Fallback } from './Layout/Fallback';
import RoleBasedLayout from './Layout/RoleBasedLayout';
import { useSelector } from 'react-redux';
import { onForegroundMessage, registerFcmToken } from './components/firebaseConfig';
import { queryClient } from './providers/QueryProvider';

const clientRoutes = [
  {
    layout: SidebarLayout,
    exact: true,
    path: "/",
    element: lazy(() => import("./components/client/Pages/Home/Index")),
  },
  {
    exact: true,
    path: "/signup",
    element: lazy(() => import("./components/client/auth/SignUp/SignUp")),
  },
  {
    exact: true,
    path: "/register",
    element: lazy(() => import("./components/client/auth/Register/Register")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/user-dashboard",
    element: lazy(() => import("./components/client/Pages/user-dashboard/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/shipment",
    element: lazy(() => import("./components/client/Pages/shipment/AllShipments")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/shipment-create",
    element: lazy(() => import("./components/client/Pages/shipment/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/all-shipments",
    element: lazy(() => import("./components/client/Pages/shipment/AllShipments")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/carrier/shipments",
    element: lazy(() => import("./components/client/Pages/shipment/AllCarrierShipment")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/shipment/:id?",
    element: lazy(() => import("./components/client/Pages/shipment/Bids")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/track-shipment",
    element: lazy(() => import("./components/client/Pages/shipment/TrackShipment")),
  },
  {
    layout: RoleBasedLayout,
    exact: true,
    path: "/payment",
    element: lazy(() => import("./components/client/Pages/payment/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/my-account",
    element: lazy(() => import("./components/client/Pages/myaccount/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/transactions",
    element: lazy(() => import("./components/client/Pages/wallet/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/bank-details",
    element: lazy(() => import("./components/client/Pages/banks/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/invoices-pods",
    element: lazy(() => import("./components/client/Pages/invoice/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    exact: true,
    path: "/message/:id?",
    element: lazy(() => import("./components/client/Pages/message/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    exact: true,
    path: "/chat/:id?",
    element: lazy(() => import("./components/client/Pages/message/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/carrer-dashboard",
    element: lazy(() => import("./components/client/Pages/carrier-dashboard/Index")),
  },

  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/company-info",
    element: lazy(() => import("./components/client/Pages/carrier/CompanyProfile")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/fleet",
    element: lazy(() => import("./components/client/Pages/carrier/Fleet")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/drivers",
    element: lazy(() => import("./components/client/Pages/carrier/Drivers")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/review",
    element: lazy(() => import("./components/client/Pages/carrier/Review")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/loads",
    element: lazy(() => import("./components/client/Pages/carrier/loads/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/bids",
    element: lazy(() => import("./components/client/Pages/carrier/mybids/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/carrier/transactions",
    element: lazy(() => import("./components/client/Pages/carrier/wallet/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/document-management",
    element: lazy(() => import("./components/client/Pages/carrier/document-management/Index")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/all-notification",
    element: lazy(() => import("./components/client/NotificationDropdown/NotificationList")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/stripe/onboarding/refresh",
    element: lazy(() => import("./components/client/Pages/wallet/StripeOnboardingRefresh")),
  },
  {
    guard: PrivateGuard,
    guardProps: { allowedRoles: ['CUSTOMER', 'SHIPPER'] },
    layout: RoleBasedLayout,
    exact: true,
    path: "/stripe/onboarding/complete",
    element: lazy(() => import("./components/client/Pages/wallet/StripeOnboardingComplete")),
  },
];

function ClientApp() {

  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return;
    registerFcmToken();

    const refreshBell = () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] });

    let unsubscribe = () => { };
    let cancelled = false;
    onForegroundMessage(refreshBell).then((u) => {
      if (cancelled) u(); else unsubscribe = u;
    });

    const onSwMessage = (e) => {
      if (e.data?.type === "FCM_NEW_NOTIFICATION") refreshBell();
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);

    window.addEventListener("focus", refreshBell);

    return () => {
      cancelled = true;
      unsubscribe();
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
      window.removeEventListener("focus", refreshBell);
    };
  }, [isAuthenticated]);

  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        {clientRoutes.map((route, i) => {
          const Guard = route?.guard || Fragment;
          const guardProps = route?.guardProps || {};
          const Layout = route?.layout || Fragment;
          const Component = route.element;
          return (
            <Route
              key={i}
              path={route.path}
              element={
                <Guard {...guardProps}>
                  <Layout>
                    <Component />
                  </Layout>
                </Guard>
              }
            />
          );
        })}
      </Routes>
    </Suspense>
  );
}

export default ClientApp;